import { deepOrange } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';

import { BASE_TRANSITION } from '../constants';

// Расширение типов для кастомного варианта кнопки
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    dashed: true;
  }
}

// Расширение типов для палитры
declare module '@mui/material/styles' {
  interface PaletteColor {
    dashed?: string;
  }
}

const MuiButton: Components<Theme>['MuiButton'] = {
  variants: [
    {
      /**
       * Кастомный вариант кнопки с пунктирной рамкой
       * @param root0 Пропсы
       * @param root0.theme Тема
       */
      props: { variant: 'dashed' },
      /**
       * Стили для кастомного варианта кнопки
       * @param root0 Пропсы
       * @param root0.theme Тема
       */
      style: ({ theme }) => ({
        border: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
        color: theme.palette.primary.main,
        transition: BASE_TRANSITION,
        textTransform: 'none',
        ':hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04)
        }
      })
    }
  ],
  styleOverrides: {
    /**
     * Корневые стили кнопки
     * @param root0 Корневые пропсы
     * @param root0.theme Тема
     */
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderL,
      transition: BASE_TRANSITION,
      textTransform: 'none'
    }),
    /**
     * Стили для кнопки варианта contained и цвета secondary
     * @param root0 Пропсы кнопки
     * @param root0.theme Тему
     */
    containedSecondary: ({ theme }) => ({
      color: theme.palette.common.white,
      position: 'relative',
      ':enabled': {
        backgroundSize: '200%',
        backgroundPosition: 'right',
        backgroundImage: theme.palette.gradients.secondary
      },
      ':hover': {
        backgroundPosition: 'left'
      },
      ':disabled': {
        background: deepOrange[100],
        color: theme.palette.common.white
      }
    }),
    /**
     * Стили для кнопки варианта outlined и цвета secondary
     * @param root0 Пропсы
     * @param root0.theme Тема
     */
    outlinedSecondary: ({ theme }) => ({
      position: 'relative',
      color: 'inherit',
      background: 'transparent',
      border: 'none',
      borderRadius: theme.shape.borderL,
      transition: BASE_TRANSITION,
      zIndex: 0,
      overflow: 'hidden',

      // создаём рамку как отдельный слой
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        padding: '1px', // толщина рамки
        borderRadius: theme.shape.borderL,
        background: `radial-gradient(circle at right 0% top -10%, 
                     ${theme.palette.secondary.light} 0%, 
                     ${theme.palette.secondary.main} 32%, 
                     ${theme.palette.secondary.dark} 100%)`,
        WebkitMask:
          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none',
        zIndex: -1
      },

      ':hover::before': {
        background: `radial-gradient(circle at right 0% top -10%, 
                     ${theme.palette.secondary.dark} 0%, 
                     ${theme.palette.secondary.main} 32%, 
                     ${theme.palette.secondary.light} 100%)`
      }
    }),
    /**
     * Стили для кнопки варианта outlined
     * @param root0 Пропсы
     * @param root0.theme Тема
     */
    outlined: ({ theme }) => ({
      background: theme.palette.custom.background
    }),
    /**
     * Стили кнопки размера large
     *
     * Жёсткая height не давала тап-размер: `size='large'` по всему приложению
     * рендерил 34px. Ниже md добираем `minHeight` до 44px — сама height
     * остаётся прежней, поэтому на десктопе кнопки не меняются.
     * @param root0 Пропсы
     * @param root0.theme Тема
     */
    sizeLarge: ({ theme }) => ({
      height: 34,
      [theme.breakpoints.down('md')]: {
        minHeight: 44
      }
    }),
    /**
     * Стили кнопки размера medium
     * @param root0 Пропсы
     * @param root0.theme Тема
     */
    sizeMedium: ({ theme }) => ({
      height: 28,
      fontSize: 14,
      fontWeight: 500,
      [theme.breakpoints.down('md')]: {
        minHeight: 44
      }
    })
  }
};

export const button = { MuiButton };
